/**
 * AI Rename Suggest prototype - shared RenameField for chat + documents
 * Access: http://flownotes.localhost/?test=ai-rename-suggest
 * Bet: docs/product/bets/2026-07-10-ai-rename-suggest.md
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Calendar,
  Hash,
  Loader2,
  Sparkles,
  MessageSquare,
  FileText,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type Scene = 'chat-sidebar' | 'doc-basecamp' | 'doc-plain' | 'doc-modal'

const SCENES: { id: Scene; label: string; hint: string }[] = [
  {
    id: 'chat-sidebar',
    label: 'Chat sidebar',
    hint: 'Inline rename in Chats list (primary surface today)',
  },
  {
    id: 'doc-basecamp',
    label: 'Doc + Basecamp',
    hint: 'Date + todo ID chips when frontmatter has todo_id',
  },
  {
    id: 'doc-plain',
    label: 'Doc plain',
    hint: 'Date chip only - no todo chip without frontmatter id',
  },
  {
    id: 'doc-modal',
    label: 'Doc modal',
    hint: 'Title-first: checkboxes, sparkle on title, Apply from title → filename',
  },
]

const CHAT_SLUGS = [
  'Workspace Context and Scope Overview',
  'Workspace context cascade',
  'Soul tiers and folder scope',
  'Context cascade walkthrough',
]

const DOC_TITLES = [
  'Kindred Projects Ethics Systems Technology',
  'Kindred Ethics Systems Research',
  'Kootenay Kindred Project Scan',
  'Ethics Systems Technology Notes',
]

const DOC_SLUGS = [
  'kindred-projects-ethics-systems-technology',
  'kindred-ethics-systems-research',
  'kootenay-kindred-project-scan',
  'ethics-systems-technology-notes',
]

const MOCK_TODO_ID = '9921842247'
const MOCK_DATE = '2026-07-10'

function stripKnownPrefixes(name: string, todoId: string | null): string {
  let rest = name.trim()
  if (todoId) {
    const todoRe = new RegExp(`^${todoId}-`)
    rest = rest.replace(todoRe, '')
  }
  rest = rest.replace(/^\d{4}-\d{2}-\d{2}-/, '')
  return rest
}

function buildName(opts: {
  slug: string
  dateOn: boolean
  todoOn: boolean
  todoId: string | null
}): string {
  const parts: string[] = []
  if (opts.todoOn && opts.todoId) parts.push(opts.todoId)
  if (opts.dateOn) parts.push(MOCK_DATE)
  parts.push(opts.slug)
  return parts.join('-')
}

interface RenameFieldProps {
  value: string
  onChange: (value: string) => void
  onSuggest: () => void
  isSuggesting?: boolean
  suggestLabel?: string
  dateOn?: boolean
  onDateToggle?: () => void
  showDateChip?: boolean
  todoId?: string | null
  todoOn?: boolean
  onTodoToggle?: () => void
  autoFocus?: boolean
  className?: string
}

type PrefixChunk = 'todo' | 'date'

type NameSegment = { key: PrefixChunk | 'slug'; text: string }

/** Split filename into todo / date / slug segments for hover highlight. */
function segmentFilename(value: string, todoId: string | null): NameSegment[] {
  const segments: NameSegment[] = []
  let rest = value

  if (todoId && (rest === todoId || rest.startsWith(`${todoId}-`))) {
    const withHyphen = rest.startsWith(`${todoId}-`)
    segments.push({ key: 'todo', text: withHyphen ? `${todoId}-` : todoId })
    rest = withHyphen ? rest.slice(todoId.length + 1) : ''
  }

  const dateMatch = rest.match(/^(\d{4}-\d{2}-\d{2})(-)?/)
  if (dateMatch) {
    const full = dateMatch[0]
    segments.push({ key: 'date', text: full })
    rest = rest.slice(full.length)
  }

  if (rest.length > 0 || segments.length === 0) {
    segments.push({ key: 'slug', text: rest })
  }

  return segments
}

/** Prototype RenameField - sparkle + optional date/todo chips (mock only). */
function RenameField({
  value,
  onChange,
  onSuggest,
  isSuggesting = false,
  suggestLabel = 'AI suggest name',
  dateOn,
  onDateToggle,
  showDateChip = false,
  todoId = null,
  todoOn,
  onTodoToggle,
  autoFocus = false,
  className,
}: RenameFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [hoverChunk, setHoverChunk] = useState<PrefixChunk | null>(null)

  useEffect(() => {
    if (!autoFocus) return
    const t = window.setTimeout(() => {
      inputRef.current?.focus()
      inputRef.current?.select()
    }, 50)
    return () => window.clearTimeout(t)
  }, [autoFocus])

  const showTodoChip = Boolean(todoId)
  const segments = useMemo(() => segmentFilename(value, todoId), [value, todoId])
  const showMirror = hoverChunk !== null

  return (
    <div className={cn('space-y-2', className)}>
      <div className="relative flex items-center">
        {/* Mirror layer: bold/highlight the chunk matching the hovered active chip */}
        {showMirror && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 flex items-center overflow-hidden rounded-md py-2 pl-3 pr-10 text-sm"
          >
            <span className="truncate font-mono">
              {segments.map((seg, i) => {
                const active = seg.key === hoverChunk
                return (
                  <span
                    key={`${seg.key}-${i}`}
                    className={cn(
                      active
                        ? 'font-bold text-primary underline decoration-primary/50 underline-offset-2'
                        : 'text-foreground/80',
                    )}
                  >
                    {seg.text}
                  </span>
                )
              })}
            </span>
          </div>
        )}
        <input
          ref={inputRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'w-full rounded-md border border-input bg-background py-2 pl-3 pr-10 font-mono text-sm outline-none focus:ring-1 focus:ring-primary',
            showMirror && 'caret-foreground text-transparent',
          )}
          aria-label="New name"
        />
        <button
          type="button"
          onClick={onSuggest}
          disabled={isSuggesting}
          title={suggestLabel}
          aria-label={suggestLabel}
          className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
        >
          {isSuggesting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
        </button>
      </div>

      {(showDateChip || showTodoChip) && (
        <div className="flex flex-wrap items-center gap-2">
          {showTodoChip && todoId && (
            <button
              type="button"
              onClick={onTodoToggle}
              onMouseEnter={() => {
                if (todoOn) setHoverChunk('todo')
              }}
              onMouseLeave={() => setHoverChunk(null)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                todoOn
                  ? 'border-primary/40 bg-primary/10 text-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground opacity-60',
              )}
              title={todoOn ? 'Remove Basecamp todo id prefix' : 'Add Basecamp todo id prefix'}
            >
              <Hash className="h-3 w-3 shrink-0" />
              {todoId}
            </button>
          )}
          {showDateChip && (
            <button
              type="button"
              onClick={onDateToggle}
              onMouseEnter={() => {
                if (dateOn) setHoverChunk('date')
              }}
              onMouseLeave={() => setHoverChunk(null)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                dateOn
                  ? 'border-primary/40 bg-primary/10 text-foreground'
                  : 'border-border bg-muted/40 text-muted-foreground opacity-60',
              )}
              title={dateOn ? 'Remove date prefix' : 'Add date prefix'}
            >
              <Calendar className="h-3 w-3 shrink-0" />
              {MOCK_DATE}
            </button>
          )}
          <span className="text-[11px] text-muted-foreground">
            Hover an active chip to highlight that prefix. Sparkle shuffles the slug body.
          </span>
        </div>
      )}
    </div>
  )
}

function useMockSuggest(pool: string[]) {
  const [index, setIndex] = useState(0)
  const [isSuggesting, setIsSuggesting] = useState(false)

  const suggest = useCallback(async (): Promise<string> => {
    setIsSuggesting(true)
    await new Promise((r) => setTimeout(r, 450))
    const next = (index + 1) % pool.length
    setIndex(next)
    setIsSuggesting(false)
    return pool[next]
  }, [index, pool])

  return { suggest, isSuggesting, index }
}

/** Chat sidebar mock row with inline RenameField. */
function ChatSidebarScene() {
  const [renaming, setRenaming] = useState(true)
  const [value, setValue] = useState(CHAT_SLUGS[0])
  const { suggest, isSuggesting } = useMockSuggest(CHAT_SLUGS)

  const onSuggest = async () => {
    const next = await suggest()
    setValue(next)
  }

  return (
    <div className="mx-auto max-w-sm rounded-lg border border-border bg-card">
      <div className="border-b border-border px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Today
      </div>
      <div className="space-y-0.5 p-1">
        <div className="rounded-md px-2 py-2 text-sm text-muted-foreground">
          Ingesting: What does the Pathway...
          <div className="text-[11px]">1m ago</div>
        </div>
        <div className="rounded-md border border-primary/30 bg-accent/40 px-2 py-2">
          {renaming ? (
            <>
              <RenameField
                value={value}
                onChange={setValue}
                onSuggest={() => void onSuggest()}
                isSuggesting={isSuggesting}
                suggestLabel="AI suggest title (click again to shuffle)"
                autoFocus
              />
              <div className="mt-2 flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setRenaming(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => setRenaming(false)}>
                  Save
                </Button>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="w-full text-left"
              onClick={() => setRenaming(true)}
            >
              <p className="truncate text-sm font-medium">{value}</p>
              <p className="text-[11px] text-muted-foreground">2h ago - click to rename</p>
            </button>
          )}
        </div>
        <div className="rounded-md px-2 py-2 text-sm text-muted-foreground">
          Ingesting: Todo 99218...
          <div className="text-[11px]">2h ago</div>
        </div>
      </div>
    </div>
  )
}

/** Document rename field with optional Basecamp todo chip. */
function DocRenameScene({ withTodo }: { withTodo: boolean }) {
  const todoId = withTodo ? MOCK_TODO_ID : null
  const initialSlug = DOC_SLUGS[0]
  const [dateOn, setDateOn] = useState(withTodo ? false : true)
  const [todoOn, setTodoOn] = useState(withTodo)
  const [slug, setSlug] = useState(initialSlug)
  const { suggest, isSuggesting } = useMockSuggest(DOC_SLUGS)

  const value = useMemo(
    () => buildName({ slug, dateOn, todoOn, todoId }),
    [slug, dateOn, todoOn, todoId],
  )

  const onChange = (next: string) => {
    setSlug(stripKnownPrefixes(next, todoId))
  }

  const onSuggest = async () => {
    const nextSlug = await suggest()
    setSlug(nextSlug)
  }

  const toggleDate = () => {
    setDateOn((on) => !on)
  }

  const toggleTodo = () => {
    setTodoOn((on) => !on)
  }

  return (
    <div className="mx-auto max-w-xl space-y-3 rounded-lg border border-border bg-card p-4">
      <div className="text-xs text-muted-foreground">
        Kootenay Lightweb (Shared)
        {withTodo ? (
          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
            todo_id: {MOCK_TODO_ID}
          </span>
        ) : (
          <span className="ml-2 text-[10px]">no todo_id in frontmatter</span>
        )}
      </div>
      <RenameField
        value={value}
        onChange={onChange}
        onSuggest={() => void onSuggest()}
        isSuggesting={isSuggesting}
        showDateChip
        dateOn={dateOn}
        onDateToggle={toggleDate}
        todoId={todoId}
        todoOn={todoOn}
        onTodoToggle={toggleTodo}
        autoFocus
      />
      <p className="font-mono text-xs text-muted-foreground">
        Preview path: .../todos/{value}.md
      </p>
    </div>
  )
}

/** Doc rename modal - title-first with checkboxes + apply from title. */
function DocModalScene() {
  const [open, setOpen] = useState(true)
  const todoId = MOCK_TODO_ID
  const [updateTitle, setUpdateTitle] = useState(true)
  const [updateFilename, setUpdateFilename] = useState(true)
  const [dateOn, setDateOn] = useState(false)
  const [todoOn, setTodoOn] = useState(true)
  const [title, setTitle] = useState(DOC_TITLES[0])
  const [slug, setSlug] = useState(DOC_SLUGS[0])
  const { suggest, isSuggesting } = useMockSuggest(DOC_TITLES)

  const value = useMemo(
    () => buildName({ slug, dateOn, todoOn, todoId }),
    [slug, dateOn, todoOn, todoId],
  )

  if (!open) {
    return (
      <div className="flex justify-center">
        <Button onClick={() => setOpen(true)}>Open rename modal</Button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md rounded-lg border border-border bg-card shadow-lg">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-sm font-semibold">Rename</h2>
        <button
          type="button"
          className="text-muted-foreground hover:text-foreground"
          onClick={() => setOpen(false)}
          aria-label="Close"
        >
          x
        </button>
      </div>
      <div className="space-y-4 p-4">
        <p className="text-xs text-muted-foreground">
          Sparkle suggests Title Case. Apply from title slugifies into the filename.
        </p>

        <div className="space-y-2 rounded-md border border-border p-3">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input
              type="checkbox"
              checked={updateTitle}
              onChange={(e) => setUpdateTitle(e.target.checked)}
            />
            Document title
          </label>
          <div className="relative">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={!updateTitle}
              className="w-full rounded-md border border-input bg-background py-2 pl-3 pr-10 text-sm outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            <button
              type="button"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-muted-foreground hover:bg-accent"
              onClick={async () => {
                const next = await suggest()
                setTitle(next)
              }}
              disabled={isSuggesting || !updateTitle}
              title="AI suggest title"
            >
              {isSuggesting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
            </button>
          </div>
        </div>

        <div className="space-y-2 rounded-md border border-border p-3">
          <div className="flex items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={updateFilename}
                onChange={(e) => setUpdateFilename(e.target.checked)}
              />
              Filename
            </label>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => {
                setSlug(
                  title
                    .toLowerCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/[\s_]+/g, '-')
                    .replace(/-+/g, '-')
                    .replace(/^-+|-+$/g, ''),
                )
                setUpdateFilename(true)
              }}
              disabled={!title.trim()}
            >
              Apply from title
            </Button>
          </div>
          <RenameField
            value={value}
            onChange={(next) => setSlug(stripKnownPrefixes(next, todoId))}
            showSuggest={false}
            showDateChip
            dateOn={dateOn}
            onDateToggle={() => setDateOn((v) => !v)}
            todoId={todoId}
            todoOn={todoOn}
            onTodoToggle={() => setTodoOn((v) => !v)}
            disabled={!updateFilename}
          />
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Rename
          </Button>
        </div>
      </div>
    </div>
  )
}

/** Prototype harness for shared AI rename field. */
export function TestAiRenameSuggest() {
  const [scene, setScene] = useState<Scene>('chat-sidebar')
  const meta = SCENES.find((s) => s.id === scene)

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-6 w-6 text-primary" />
          <div>
            <h1 className="text-xl font-semibold">AI Rename Suggest</h1>
            <p className="text-sm text-muted-foreground">
              Shared RenameField prototype - sparkle preview/shuffle, date + Basecamp
              todo chips. Mock only (?test=ai-rename-suggest).
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Bet:{' '}
              <code className="rounded bg-muted px-1">
                docs/product/bets/2026-07-10-ai-rename-suggest.md
              </code>
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {SCENES.map((s) => (
            <Button
              key={s.id}
              size="sm"
              variant={scene === s.id ? 'default' : 'outline'}
              onClick={() => setScene(s.id)}
            >
              {s.id.startsWith('chat') ? (
                <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
              ) : (
                <FileText className="mr-1.5 h-3.5 w-3.5" />
              )}
              {s.label}
            </Button>
          ))}
        </div>

        {meta && (
          <p className="text-sm text-muted-foreground">{meta.hint}</p>
        )}

        {scene === 'chat-sidebar' && <ChatSidebarScene />}
        {scene === 'doc-basecamp' && <DocRenameScene withTodo />}
        {scene === 'doc-plain' && <DocRenameScene withTodo={false} />}
        {scene === 'doc-modal' && <DocModalScene />}

        <div className="rounded-md border border-dashed border-border p-4 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">Try</p>
          <ul className="list-disc space-y-1 pl-4">
            <li>Click sparkle - fills a preview name (editable)</li>
            <li>Click sparkle again - shuffles to another mock slug</li>
            <li>Toggle date / todo chips - prefixes add/remove; slug body stays</li>
            <li>Hover an active chip - that prefix bolds/highlights in the input</li>
            <li>Inactive chips are muted only (no strikethrough)</li>
            <li>Doc plain scene - todo chip hidden (no frontmatter id)</li>
          </ul>
        </div>
      </div>
    </div>
  )
}
